import { useState, useCallback, useRef, useEffect } from 'react';
import { useSSEStream } from '../../hooks/useSSEStream';
import useChatSessions from './useChatSessions';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';
import { MESSAGE_ROLES, STEP_STATUS } from '../constants';

const EVENT_TYPES = {
    // Lifecycle events
    STARTED: 'report_generation_started',
    COMPLETED: 'report_generation_completed',
    ERROR: 'report_error',
    CANCELLED: 'report_cancelled',

    // Content events
    TOKEN: 'token',
    TOKEN_BATCH: 'token_batch',
    STEP_UPDATE: 'step_update',
    ARTIFACT: 'data_artifact',

    RESPONSE_COMPLETE: 'response_complete',
    SESSION_TITLE_UPDATED: 'session_title_updated',
};

const createAssistantMessage = messageId => ({
    id: messageId,
    role: MESSAGE_ROLES.ASSISTANT,
    content: [], // Content is an array of blocks: [{type: 'markdown', data: '...'}, {type: 'code', data: {...}}, ...]
    isThinking: true,
    isStreaming: false,
    createdAt: new Date(),
    steps: [],
});

const updateMessageSteps = (existingSteps, newStep) => {
    const stepIndex = existingSteps.findIndex(s => s.step_id === newStep.step_id);
    const updatedSteps =
        stepIndex >= 0
            ? existingSteps.map((step, idx) => (idx === stepIndex ? { ...step, ...newStep } : step))
            : [...existingSteps, newStep];

    return {
        steps: updatedSteps,
        isThinking: updatedSteps.some(s => s.status === STEP_STATUS.RUNNING),
    };
};

const useChat = ({ api, onFinish, onError } = {}) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentRunId, setCurrentRunId] = useState(null);
    const currentAssistantMessageIdRef = useRef(null);
    const onFinishRef = useRef(onFinish);
    const onErrorRef = useRef(onError);
    const inputRef = useRef('');

    useEffect(() => {
        onFinishRef.current = onFinish;
        onErrorRef.current = onError;
    }, [onFinish, onError]);

    useEffect(() => {
        inputRef.current = input;
    }, [input]);

    const onSessionChange = useCallback((sessionId, shouldClearMessages, loadedMessages = null) => {
        if (shouldClearMessages) {
            setMessages([]);
            setError(null);
        } else if (loadedMessages) {
            setMessages(loadedMessages);
            setError(null);
        }
    }, []);

    const {
        currentSessionId,
        setCurrentSessionId,
        sessions,
        isLoadingSessions,
        fetchSessions,
        upsertSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
    } = useChatSessions({ api, onSessionChange });

    const clearCurrentSession = useCallback(() => {
        setCurrentSessionId(null);
        setMessages([]);
        setError(null);
    }, [setCurrentSessionId]);

    const handleError = useCallback(errorMessage => {
        setError(errorMessage);
        setIsLoading(false);
        setCurrentRunId(null);
        onErrorRef.current?.(new Error(errorMessage));
    }, []);

    // Lifecycle event handlers
    const handleStarted = useCallback(() => {
        const messageId = `${MESSAGE_ROLES.ASSISTANT}-${Date.now()}`;
        currentAssistantMessageIdRef.current = messageId;
        setMessages(prev => [...prev, createAssistantMessage(messageId)]);
    }, []);

    const handleCompleted = useCallback(() => {
        setMessages(prev => {
            const updatedMessages = prev.map(msg => {
                if (msg.id !== currentAssistantMessageIdRef.current) return msg;

                const completedSteps = (msg.steps || []).map(step =>
                    step.status !== STEP_STATUS.FAILED ? { ...step, status: STEP_STATUS.COMPLETED } : step
                );
                return { ...msg, isThinking: false, isLoading: false, isStreaming: false, steps: completedSteps };
            });

            const finishedMessage = updatedMessages.find(m => m.id === currentAssistantMessageIdRef.current);
            if (finishedMessage) {
                onFinishRef.current?.(finishedMessage);
            }

            return updatedMessages;
        });
        setIsLoading(false);
        setCurrentRunId(null);
    }, []);

    const handleCancelled = useCallback(() => {
        setIsLoading(false);
        setCurrentRunId(null);
    }, []);

    // Content event handlers
    const handleToken = useCallback(message => {
        const content = message.content || message.token || '';
        if (content && currentAssistantMessageIdRef.current) {
            setMessages(prev =>
                prev.map(msg => {
                    if (msg.id !== currentAssistantMessageIdRef.current) return msg;

                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];

                    // Get the last content block
                    const lastBlock = updatedContent[updatedContent.length - 1];

                    // If last block is markdown, append to it; otherwise create new markdown block
                    if (lastBlock && lastBlock.type === 'markdown') {
                        lastBlock.data += content;
                    } else {
                        updatedContent.push({ type: 'markdown', data: content });
                    }

                    return { ...msg, content: updatedContent, isThinking: false, isStreaming: true };
                })
            );
        }
    }, []);

    const handleStepUpdate = useCallback(message => {
        const { step } = message;
        if (step && currentAssistantMessageIdRef.current) {
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === currentAssistantMessageIdRef.current
                        ? { ...msg, ...updateMessageSteps(msg.steps || [], step) }
                        : msg
                )
            );
        }
    }, []);

    const handleArtifact = useCallback(message => {
        const { artifact } = message;
        if (artifact && currentAssistantMessageIdRef.current) {
            setMessages(prev =>
                prev.map(msg => {
                    if (msg.id !== currentAssistantMessageIdRef.current) return msg;

                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];

                    // Add artifact as a content block
                    updatedContent.push(artifact);

                    return { ...msg, content: updatedContent };
                })
            );
        }
    }, []);

    const handleResponseComplete = useCallback(() => {
        if (currentAssistantMessageIdRef.current) {
            setMessages(prev =>
                prev.map(msg => {
                    if (msg.id !== currentAssistantMessageIdRef.current) return msg;

                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];

                    // Add line break to last markdown block if it exists
                    const lastBlock = updatedContent[updatedContent.length - 1];
                    if (lastBlock && lastBlock.type === 'markdown') {
                        lastBlock.data += '\n\n';
                    }

                    return { ...msg, isStreaming: false, content: updatedContent };
                })
            );
        }
    }, []);

    // Event routing
    const handleSSEMessage = useCallback(
        message => {
            const { type } = message;

            // Route lifecycle events
            if (type === EVENT_TYPES.STARTED) return handleStarted();
            if (type === EVENT_TYPES.COMPLETED) return handleCompleted();
            if (type === EVENT_TYPES.ERROR) return handleError(message.message || message.error || 'An error occurred');
            if (type === EVENT_TYPES.CANCELLED) return handleCancelled();
            if (type === EVENT_TYPES.RESPONSE_COMPLETE) return handleResponseComplete();
            if (type === EVENT_TYPES.SESSION_TITLE_UPDATED) return upsertSession(message.session_id, message.title);

            // Route content events (require active message)
            if (!currentAssistantMessageIdRef.current) return;

            if (type === EVENT_TYPES.TOKEN || type === EVENT_TYPES.TOKEN_BATCH) return handleToken(message);
            if (type === EVENT_TYPES.STEP_UPDATE) return handleStepUpdate(message);
            if (type === EVENT_TYPES.ARTIFACT) return handleArtifact(message);
        },
        [
            handleStarted,
            handleCompleted,
            handleError,
            handleCancelled,
            handleResponseComplete,
            upsertSession,
            handleToken,
            handleStepUpdate,
            handleArtifact,
        ]
    );

    const { isConnected: sseConnected, error: sseError } = useSSEStream(api, currentRunId, handleSSEMessage);

    useEffect(() => {
        if (sseError) handleError(sseError);
    }, [sseError, handleError]);

    const handleSubmit = useCallback(
        async (e, options = {}) => {
            e?.preventDefault();

            const messageContent = options?.content || inputRef.current;

            if (!messageContent.trim() || isLoading) return;

            setError(null);
            setIsLoading(true);

            const userMessage = {
                id: `${MESSAGE_ROLES.USER}-${Date.now()}`,
                role: MESSAGE_ROLES.USER,
                content: [{ type: 'markdown', data: messageContent }],
                createdAt: new Date(),
            };

            setMessages(prev => [...prev, userMessage]);
            setInput('');

            try {
                const response = await interactiveServiceFetch(`${api}/runs`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_query: messageContent,
                        request_id: `req-${Date.now()}`,
                        session_id: currentSessionId,
                    }),
                });

                if (!response.ok) {
                    throw new Error(`Failed to create run: ${response.status} ${response.statusText}`);
                }

                const result = await response.json();
                const runId = result.run_id;

                setCurrentRunId(runId);
                setCurrentSessionId(result.session_id || currentSessionId);
            } catch (err) {
                handleError(err.message || 'An error occurred');
            }
        },
        [isLoading, api, currentSessionId, setCurrentSessionId, handleError]
    );

    const append = useCallback(
        async message => {
            await handleSubmit(null, { content: message.content });
        },
        [handleSubmit]
    );

    const reload = useCallback(async () => {
        if (messages.length < 2) return;

        const lastUserMessage = [...messages].reverse().find(m => m.role === MESSAGE_ROLES.USER);
        if (!lastUserMessage) return;

        const userMessageIndex = messages.findIndex(m => m.id === lastUserMessage.id);
        setMessages(messages.slice(0, userMessageIndex + 1));

        await handleSubmit(null, { content: lastUserMessage.content });
    }, [messages, handleSubmit]);

    const stop = useCallback(async () => {
        if (currentRunId) {
            try {
                await interactiveServiceFetch(`${api}/runs/${currentRunId}/cancel`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                });
            } catch (err) {
                // Silently handle error
            }
        }
        setIsLoading(false);
        setCurrentRunId(null);
    }, [currentRunId, api]);

    const handleInputChange = useCallback(e => setInput(e.target.value), []);

    return {
        messages,
        input,
        setInput,
        handleSubmit,
        handleInputChange,
        isLoading,
        error,
        append,
        reload,
        stop,
        setMessages,
        sseConnected,
        // Session management
        currentSessionId,
        sessions,
        isLoadingSessions,
        clearCurrentSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
        fetchSessions,
    };
};

export default useChat;
